import {
  ASK_TO_CONTINUE,
  CHANGE_PASSWORD,
  DELETE_PROFILE,
  GET_PROFILE,
  LOGIN_USER,
  LOGOUT_USER,
  POST_PROFILE,
  PUT_PROFILE,
} from "../constants/actionTypes";
import {
  CHANGE_PASSWORD_API,
  GET_PROFILE_API,
  LOGIN_API,
  VERIFY_OTP_API,
  RESEND_OTP_API,
  LOGOUT_API,
  POST_PROFILE_API,
} from "../constants/apis";
import axios from "../../apiClient";
import libAxios from "axios";
import {
  handleErrorMessage,
  handleSuccessMessage,
  handleSystemErrorMessage,
} from "./utils";
import { store } from "../store";

export function postProfile(data) {
  return async (dispatch) => {
    const headers = {
      "content-type": "application/json",
      Authorization: `Token ${localStorage.getItem("token")}`,
    };
    try {
      const response = await axios.post(`${POST_PROFILE_API}`, data, {
        headers,
      });
      if (response?.status >= 200 && response?.status <= 310) {
        dispatch({ type: POST_PROFILE, payload: response?.data });
        dispatch(handleSuccessMessage("Profile is created successfully!"));
      } else {
        dispatch(handleErrorMessage(response?.error));
      }
    } catch (error) {
      dispatch(handleErrorMessage(error));
    }
  };
}

export function updateProfile(data) {
  const headers = {
    "content-type": "application/json",
    Authorization: `Token ${localStorage.getItem("token")}`,
  };
  if (Object.keys(data).includes("groups")) delete data["groups"];
  return async (dispatch) => {
    try {
      const response = await axios.patch(
        `${POST_PROFILE_API}${data?.id_user}/`,
        data,
        { headers },
      );
      if (response?.status >= 200 && response?.status <= 310) {
        dispatch({ type: PUT_PROFILE, payload: response?.data });
        dispatch(handleSuccessMessage("Profile is updated successfully!"));
      } else {
        dispatch(handleErrorMessage(response?.error));
      }
    } catch (error) {
      dispatch(handleErrorMessage(error));
    }
  };
}

export function deleteProfile(data) {
  return async (dispatch) => {
    try {
      const response = await axios.delete(`${POST_PROFILE_API}${data}/`);
      if (response?.status >= 200 && response?.status <= 310) {
        dispatch({ type: DELETE_PROFILE, payload: response?.data });
        dispatch(handleSuccessMessage("Profile is deleted successfully!"));
      } else {
        dispatch(handleErrorMessage(response?.error));
      }
    } catch (error) {
      dispatch(handleErrorMessage(error));
    }
  };
}

export function getProfile() {
  return async (dispatch) => {
    try {
      const userdata = localStorage.getItem("UserId");
      const response = await axios.get(`${GET_PROFILE_API}/${userdata}/`);
      if (response?.status >= 200 && response?.status <= 310) {
        dispatch({ type: GET_PROFILE, payload: response?.data });
      } else {
        dispatch(handleErrorMessage(response?.error));
      }
    } catch (error) {
      dispatch(handleErrorMessage(error));
    }
  };
}

function _persistLogin(response) {
  localStorage.setItem("token", response?.data?.token);
  localStorage.setItem("UserId", JSON.stringify(response?.data?.id_user));
  const timeNow = Date.now();
  localStorage.setItem("lastLoggedInTime", timeNow);
  localStorage.setItem("lastValidatedLoggedInTime", timeNow);
  if (response?.data?.trusted_device_token) {
    localStorage.setItem(
      "trusted_device_token",
      response?.data?.trusted_device_token,
    );
  }
}

function _serverErrorMessage(error, fallback) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    (typeof error?.response?.data === "string"
      ? error.response.data
      : null) ||
    fallback
  );
}

export function loginUser(data) {
  return async (dispatch) => {
    try {
      const payload = { ...data };
      const trusted = localStorage.getItem("trusted_device_token");
      if (trusted && !payload.trusted_device_token) {
        payload.trusted_device_token = trusted;
      }
      const response = await libAxios.post(`${LOGIN_API}`, payload);
      if (response?.status >= 200 && response?.status <= 310) {
        // Step 1 2FA: new IP -> backend requires OTP, no token yet
        if (response?.data?.requires_2fa) {
          sessionStorage.setItem(
            "pending_challenge_id",
            response?.data?.challenge_id,
          );
          if (response?.data?.masked_email) {
            sessionStorage.setItem(
              "pending_masked_email",
              response?.data?.masked_email,
            );
          }
          dispatch(
            handleSuccessMessage(
              "A verification code has been sent to your email. Valid for 10 minutes.",
            ),
          );
          return response?.data;
        }
        _persistLogin(response);
        dispatch({ type: LOGIN_USER, payload: {} });
        dispatch(getProfile());
        dispatch(handleSuccessMessage("User is logged in successfully!"));
        return response?.data;
      } else {
        dispatch(handleErrorMessage(response?.error));
        return null;
      }
    } catch (error) {
      dispatch(
        handleErrorMessage(_serverErrorMessage(error, "Login failed.")),
      );
      return null;
    }
  };
}

export function verifyOtp(data) {
  return async (dispatch) => {
    try {
      const response = await libAxios.post(`${VERIFY_OTP_API}`, data);
      if (response?.status >= 200 && response?.status <= 310) {
        _persistLogin(response);
        sessionStorage.removeItem("pending_challenge_id");
        sessionStorage.removeItem("pending_masked_email");
        dispatch({ type: LOGIN_USER, payload: {} });
        dispatch(getProfile());
        dispatch(handleSuccessMessage("Verification successful. Welcome!"));
        return response?.data;
      } else {
        dispatch(handleErrorMessage(response?.error));
        return null;
      }
    } catch (error) {
      dispatch(
        handleErrorMessage(
          _serverErrorMessage(error, "Invalid verification code."),
        ),
      );
      return null;
    }
  };
}

export function resendOtp(challenge_id) {
  return async (dispatch) => {
    try {
      const response = await libAxios.post(`${RESEND_OTP_API}`, {
        challenge_id,
      });
      if (response?.status >= 200 && response?.status <= 310) {
        dispatch(
          handleSuccessMessage("A new code has been sent to your email."),
        );
        return response?.data;
      } else {
        dispatch(handleErrorMessage(response?.error));
        return null;
      }
    } catch (error) {
      dispatch(
        handleErrorMessage(
          _serverErrorMessage(error, "Unable to resend the code."),
        ),
      );
      return null;
    }
  };
}

export function changeUserPassword(data) {
  const headers = {
    "content-type": "application/json",
    Authorization: `Token ${localStorage.getItem("token")}`,
  };
  return async (dispatch) => {
    try {
      const response = await axios.post(`${CHANGE_PASSWORD_API}`, data, {
        headers,
      });
      if (response?.status >= 200 && response?.status <= 310) {
        dispatch(
          loginUser({ username: email, password: data?.new_password }),
        ).then(() =>
          dispatch(
            handleSuccessMessage("The password is changed successfully!"),
          ),
        );
      } else {
        dispatch(handleErrorMessage(response?.error));
      }
    } catch (error) {
      dispatch(handleErrorMessage(error));
    }
  };
}

export function logoutUser() {
  return async (dispatch) => {
    try {
      const userId = localStorage.getItem("UserId")
      const response = await libAxios.patch(`${LOGOUT_API}?user_id=${userId}`);
      if (response?.status >= 200 && response?.status <= 310) {
        localStorage.removeItem("token");
        localStorage.removeItem("UserId");
        localStorage.removeItem("lastLoggedInTime");
        localStorage.removeItem("lastValidatedLoggedInTime");
        dispatch({ type: LOGOUT_USER, payload: {} });
        dispatch(handleSuccessMessage("User is logged out successfully!"));
      } else {
        dispatch(handleErrorMessage(response?.error));
      }
    } catch (error) {
      const timeNow = Date.now();
      localStorage.setItem("lastLoggedInTime", timeNow);
      localStorage.setItem("lastValidatedLoggedInTime", timeNow);
      dispatch(handleErrorMessage(error));
    }
  };
}
